import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Bell, BellOff, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import usePushNotifications from "@/hooks/usePushNotifications";

const CATEGORY_LABELS = {
  new_payment: "Nouveau paiement reçu",
  overdue_orders: "Commandes en retard",
  seller_objective: "Objectif vendeur atteint / en retard",
  equipment_expiring: "Équipement / véhicule arrivant à expiration",
  backup_reminder: "Confirmation de sauvegarde quotidienne",
};

export const PushSection = () => {
  const { supported, permission, subscribed, loading, error, subscribe, unsubscribe, sendTest } =
    usePushNotifications();
  const [prefs, setPrefs] = useState(null);
  const [deviceCount, setDeviceCount] = useState(0);
  const [busy, setBusy] = useState(false);

  const refreshStatus = async () => {
    try {
      const { data } = await api.getPushStatus();
      setPrefs(data.preferences || {});
      setDeviceCount(data.active_subscriptions || 0);
    } catch (_e) {
      /* silent */
    }
  };

  useEffect(() => {
    refreshStatus();
  }, [subscribed]);

  const handleToggle = async () => {
    setBusy(true);
    if (subscribed) {
      const ok = await unsubscribe();
      if (ok) toast.success("Notifications désactivées sur cet appareil");
    } else {
      const ok = await subscribe();
      if (ok) toast.success("Notifications activées");
    }
    setBusy(false);
    await refreshStatus();
  };

  const handleTest = async () => {
    const ok = await sendTest();
    if (ok) toast.success("Notification de test envoyée");
    else toast.error("Impossible d'envoyer la notification de test");
  };

  const handlePrefChange = async (key, value) => {
    const next = { ...(prefs || {}), [key]: value };
    setPrefs(next);
    try {
      await api.updatePushPreferences(next);
    } catch (e) {
      toast.error("Échec de sauvegarde des préférences");
    }
  };

  return (
    <Card data-testid="push-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-heading">
          <Bell className="h-5 w-5 text-primary" />
          Notifications push (navigateur)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!supported ? (
          <div className="p-3 rounded-sm border border-amber-300 bg-amber-50 text-amber-900 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p className="text-sm">
              Votre navigateur ne supporte pas les notifications push. Utilisez Chrome, Edge ou Firefox récents.
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-sm bg-muted/30 border">
              <div>
                <div className="font-medium flex items-center gap-2 flex-wrap">
                  {subscribed ? (
                    <Badge variant="outline" className="border-green-400 text-green-700">
                      <Bell className="h-3 w-3 mr-1" /> Activé sur cet appareil
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-slate-300 text-slate-600">
                      <BellOff className="h-3 w-3 mr-1" /> Désactivé
                    </Badge>
                  )}
                  {deviceCount > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {deviceCount} appareil(s) abonné(s) sur votre compte
                    </span>
                  )}
                </div>
                {permission === "denied" && (
                  <p className="text-xs text-amber-700 mt-1">
                    Permission refusée : réautorisez-les dans les paramètres du site (cadenas à gauche de l'URL).
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleToggle}
                  disabled={busy || loading || permission === "denied"}
                  variant={subscribed ? "outline" : "default"}
                  data-testid={subscribed ? "push-disable-btn" : "push-enable-btn"}
                >
                  {subscribed ? (
                    <>
                      <BellOff className="h-4 w-4 mr-2" /> Désactiver
                    </>
                  ) : (
                    <>
                      <Bell className="h-4 w-4 mr-2" /> Activer
                    </>
                  )}
                </Button>
                {subscribed && (
                  <Button variant="outline" onClick={handleTest} data-testid="push-test-btn">
                    <Send className="h-4 w-4 mr-2" /> Test
                  </Button>
                )}
              </div>
            </div>

            {error && (
              <div className="text-xs text-destructive" data-testid="push-error">
                {error}
              </div>
            )}

            {prefs && (
              <div className="space-y-2 pt-2 border-t">
                <p className="text-sm font-medium">Types de notifications</p>
                {Object.keys(CATEGORY_LABELS).map((key) => (
                  <div
                    key={key}
                    className="flex items-center justify-between text-sm py-1"
                    data-testid={`push-pref-${key}`}
                  >
                    <Label htmlFor={`push-${key}`} className="cursor-pointer">
                      {CATEGORY_LABELS[key]}
                    </Label>
                    <Switch
                      id={`push-${key}`}
                      checked={!!prefs[key]}
                      onCheckedChange={(v) => handlePrefChange(key, v)}
                    />
                  </div>
                ))}
                <p className="text-xs text-muted-foreground pt-1">
                  Les préférences s'appliquent à tous vos appareils abonnés.
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PushSection;
